import React, { useEffect } from "react";
import MaintenanceGraphs from "../../components/graph/MaintenanceGraphs";
import {
  useGetAcademyQuery,
  useGetUserByEmailQuery,
} from "./../../slices/userApiSlice";
import { useGetRepairRequestQuery } from "../../slices/maintenanceApiSlice";
import {
  Building2,
  Wrench,
  CheckCircle2,
  Clock,
  TrendingUp,
  Calendar,
} from "lucide-react";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";
import { toast } from "sonner";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../../components/ui/avatar";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Separator } from "../../components/ui/separator";

const selectUserInfo = (state) => state.auth.userInfo || {};
const getUserEmail = createSelector(
  selectUserInfo,
  (userInfo) => userInfo?.username || "",
);

const CfmHome = () => {
  const { data: academies, error } = useGetAcademyQuery();
  const { data: repairRequest } = useGetRepairRequestQuery();

  const email = useSelector(getUserEmail);
  const { data: userData } = useGetUserByEmailQuery(email, {
    skip: !email,
  });

  useEffect(() => {
    if (error) {
      toast.error("Error Fetching Data", {
        description:
          "Unable to retrieve academies at this time. Please try again later.",
      });
    }
  }, [error]);

  const totalRepair = repairRequest?.filter((req) => req.accept === true);

  const totalResolvedRepair = totalRepair?.filter(
    (req) => req.status === "Completed",
  );
  const totalUnResolvedRepair = totalRepair?.filter(
    (req) => req.status === "Pending" || req.status === "In Progress",
  );

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
  };

  const getFormattedDate = () => {
    const today = new Date();
    const options = { weekday: "long", month: "long", day: "numeric" };
    return today.toLocaleDateString("en-US", options);
  };

  const greeting = getGreeting();
  const formattedDate = getFormattedDate();
  const name = userData?.user?.name || "User";
  const userProfileImage = userData?.user?.image || null;

  // Get initials for avatar fallback
  const getInitials = (name) => {
    if (!name) return "U";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const statsCards = [
    {
      title: "Total Registered Academies",
      value: academies?.length || 0,
      icon: Building2,
      description: "Active academies in the system",
      color: "bg-blue-500",
      lightColor: "bg-blue-50",
      textColor: "text-blue-600",
    },
    {
      title: "Total Maintenance Requests",
      value: totalRepair?.length || 0,
      icon: Wrench,
      description: "All accepted requests",
      color: "bg-amber-500",
      lightColor: "bg-amber-50",
      textColor: "text-amber-600",
    },
    {
      title: "Resolved Requests",
      value: totalResolvedRepair?.length || 0,
      icon: CheckCircle2,
      description: "Successfully completed",
      color: "bg-green-500",
      lightColor: "bg-green-50",
      textColor: "text-green-600",
    },
    {
      title: "Unresolved Requests",
      value: totalUnResolvedRepair?.length || 0,
      icon: Clock,
      description: "Pending or in progress",
      color: "bg-red-500",
      lightColor: "bg-red-50",
      textColor: "text-red-600",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100/50">
      <div className="p-4 sm:py-6 sm:px-8 space-y-6">
        {/* Welcome Header Card */}
        <Card className="border-none shadow-lg bg-gradient-to-r from-slate-900 to-slate-800 text-white overflow-hidden">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16 border-2 border-white/20 ring-4 ring-white/10">
                  <AvatarImage src={userProfileImage} alt={name} />
                  <AvatarFallback className="bg-white/10 text-white text-lg font-semibold">
                    {getInitials(name)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="flex items-center gap-2 text-sm text-white/70 mb-1">
                    <Calendar className="h-4 w-4" />
                    <span>{formattedDate}</span>
                  </div>
                  <h1 className="text-2xl sm:text-3xl font-bold">
                    {greeting}, {name}!
                  </h1>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Statistics Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statsCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card
                key={index}
                className="hover:shadow-lg transition-all duration-300 border-none shadow-md hover:-translate-y-1"
              >
                <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    {stat.title}
                  </CardTitle>
                  <div className={`${stat.lightColor} p-2.5 rounded-lg`}>
                    <Icon className={`h-5 w-5 ${stat.textColor}`} />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{stat.value}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {stat.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Maintenance Graphs Section */}
        <Card className="border-none shadow-lg">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl">Maintenance Overview</CardTitle>
                <CardDescription className="mt-1">
                  Track and analyze maintenance requests across all academies
                </CardDescription>
              </div>
              <TrendingUp className="h-5 w-5 text-muted-foreground" />
            </div>
          </CardHeader>
          <Separator />
          <CardContent className="pt-6">
            <MaintenanceGraphs />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CfmHome;
